import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { fetchBlogArticle } from "@/lib/blogArticles";
import { ArrowLeft, Clock4 } from "lucide-react";

export default function Article() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setArticle(null);
    setError(false);
    fetchBlogArticle(lang, slug)
      .then((data) => {
        if (!cancelled) {
          setArticle(data);
          window.scrollTo({ top: 0, behavior: "auto" });
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [lang, slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container-x py-24 text-center">
          <p className="text-zinc-600">{t.article.notFound}</p>
          <Link to="/#knowledge" className="mt-4 inline-block text-sm font-semibold text-[color:var(--sentinel)]">
            {t.article.back}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container-x py-32 text-center text-sm text-zinc-500">{t.article.loading}</div>
        <Footer />
      </div>
    );
  }

  const categoryLabel = t.knowledge.categories[article.category] || article.category;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <article className="container-x max-w-3xl py-10 md:py-14" data-testid="article-page">
        <Link
          to="/#knowledge"
          className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 transition hover:text-[color:var(--sentinel)]"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.article.back}
        </Link>

        <div className="mt-6 overflow-hidden rounded-3xl border border-zinc-200">
          <img
            src={article.image}
            alt=""
            className="aspect-[16/10] w-full object-cover"
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
          <span>{categoryLabel}</span>
          <span className="h-0.5 w-3 bg-zinc-300" />
          <span className="inline-flex items-center gap-1">
            <Clock4 className="h-3 w-3" />
            {article.readMins} {t.knowledge.readMins}
          </span>
          {article.publishedAtLabel && (
            <>
              <span className="h-0.5 w-3 bg-zinc-300" />
              <span>{article.publishedAtLabel}</span>
            </>
          )}
        </div>

        <h1 className="mt-4 text-3xl font-bold leading-tight text-zinc-900 md:text-4xl">{article.title}</h1>
        {article.excerpt && (
          <p className="mt-4 text-lg leading-relaxed text-zinc-600">{article.excerpt}</p>
        )}

        {article.biomarkers?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {article.biomarkers.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-sentinel-50 px-3 py-1 text-xs font-medium text-[color:var(--sentinel)]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="prose prose-zinc mt-10 max-w-none">
          {article.contentBlocks?.map((block, idx) => {
            const key = `${block.type}-${idx}`;
            if (block.type === "h2") {
              return (
                <h2 key={key} id={block.id || undefined} className="mt-10 text-2xl font-semibold text-zinc-900">
                  {block.text}
                </h2>
              );
            }
            if (block.type === "h3") {
              return (
                <h3 key={key} className="mt-8 text-xl font-semibold text-zinc-900">
                  {block.text}
                </h3>
              );
            }
            if (block.type === "h4") {
              return (
                <h4 key={key} className="mt-6 text-lg font-semibold text-zinc-900">
                  {block.text}
                </h4>
              );
            }
            if (block.type === "blockquote") {
              return (
                <blockquote key={key} className="border-l-4 border-[color:var(--sentinel)] pl-4 italic text-zinc-600">
                  {block.text}
                </blockquote>
              );
            }
            if (block.type === "li") {
              return (
                <li key={key} className="ml-4 text-zinc-700">
                  {block.text}
                </li>
              );
            }
            return (
              <p key={key} className="mt-4 leading-relaxed text-zinc-700">
                {block.text}
              </p>
            );
          })}
        </div>

        {article.references?.length > 0 && (
          <section className="mt-12 border-t border-zinc-200 pt-8">
            <h2 className="text-lg font-semibold text-zinc-900">{t.article.references}</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-zinc-600">
              {article.references.map((ref, idx) => (
                <li key={idx}>
                  {ref.text}{" "}
                  {ref.url && (
                    <a
                      href={ref.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[color:var(--sentinel)] underline-offset-2 hover:underline"
                    >
                      {ref.url}
                    </a>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}
      </article>
      <Footer />
    </div>
  );
}
